import { TravelTripCriteriaDisplay } from '../../types/travelInsurance';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { QuoteBasisDisclaimerTranslations } from './i18n';

export type QuoteBasisDisclaimerProps = TravelTripCriteriaDisplay & {
  /**
   * Spacing and colour, which the host owns: the two modules lay the sentence
   * out differently, and their designs disagree on the colour — Table Builder's
   * Figma says `text/secondary`, the Quick Answer prototype `text/primary`. The
   * type scale (table-cell / table-cell-normal) is the component's own and the
   * same in both.
   */
  className?: string;
};

/**
 * "a" or "an" for a trip length in digits, by how the number is *spoken*: "a
 * 5-day", but "an 8-day", "an 11-day", "an 85-day". Scoped to the reachable
 * range (1–366 days) rather than the general rule — see
 * `../../cms-blocks/TableBuilder/README.md`.
 */
const indefiniteArticleForNumber = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) return 'a';

  const days = Math.abs(Math.trunc(value));

  // The only spoken forms in 1–366 opening on a vowel sound.
  if (days === 8 || days === 11 || days === 18) return 'an';
  if (days >= 80 && days <= 89) return 'an';
  return 'a';
};

/**
 * The sentence stating the trip a module's prices are quoted for (CMS-1896 R6,
 * reused by CMS-1956).
 *
 * System-generated from the module's own travel parameters; not editable or
 * removable by editors, and never rendered outside Travel Insurance mode. It
 * belongs to no single block — Table Builder and Quick Answer both price
 * against a trip and must state it identically.
 *
 * `../../cms-blocks/TableBuilder/README.md` covers why Single Trip and Annual
 * are separate templates, why the duration is the offset + 1, and the one page
 * shape where this sentence and the prices above it disagree by design.
 */
export const QuoteBasisDisclaimer = ({
  insuranceType,
  travelAsFamily,
  destination,
  region,
  endDateOffsetDays,
  // `text-[--text-secondary]` reaches the CSS variable directly, on purpose: the
  // class `text-text/secondary` names a Tailwind theme key that the legacy
  // modulab palette shadows with its own #42424a, while the colour QA asked for
  // is the token's #424242 (CMS-1921 round 2). Do not "clean this up" into
  // `text-text/secondary` — that changes the rendered colour. It passes lint
  // only because a default parameter is not a className attribute, so
  // `tailwindcss/no-arbitrary-value` never sees it; the same string in JSX needs
  // a file-level suppression, as in `TableBuilder/components/LoanBasisNote.tsx`.
  className = 'mb-4 text-[--text-secondary]',
}: QuoteBasisDisclaimerProps) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(
    QuoteBasisDisclaimerTranslations,
    context,
  );

  // The ticket writes these as the Strapi labels ("Annual Multi Trip"); the
  // mapped enum reaching the frontend is ANNUAL_PLAN / SINGLE_PLAN.
  const isAnnual = insuranceType === 'ANNUAL_PLAN';

  const travellers = travelAsFamily
    ? staticText.disclaimer_travellers_family
    : staticText.disclaimer_travellers_single_adult;

  const template = isAnnual
    ? staticText.disclaimer_annual_trip
    : staticText.disclaimer_single_trip;

  // An offset, not a duration: the quote runs today → today + N and day 1
  // counts, so the trip quoted is N + 1 days long.
  const durationDays =
    endDateOffsetDays === undefined ? undefined : endDateOffsetDays + 1;

  // A module WITHOUT a price attribute is not publish-validated, so guard
  // against rendering "{destination}" or "undefined-day" in the copy.
  const hasRequiredValues = isAnnual
    ? !!region
    : !!destination && endDateOffsetDays !== undefined;

  if (!hasRequiredValues) return null;

  const text = template
    .replace('{article}', indefiniteArticleForNumber(durationDays))
    .replace('{duration}', String(durationDays))
    .replace('{destination}', destination ?? '')
    .replace('{region}', region ?? '')
    .replace('{travellers}', travellers);

  return (
    <p
      className={`${className} font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell`}
    >
      {text}
    </p>
  );
};
